"use client";
import { useEffect, useState, useRef } from "react";
import Webcam from "react-webcam";
import { UploadIcon, XIcon } from "lucide-react";
import {
  server_post_data,
  repairing_car_data,
  upload_image_to_folder,
} from "../../ServiceConnection/serviceconnection";
import {
  handleError,
  handleNumbersChange,
} from "../../CommonJquery/CommonJquery";

const RepairStep2 = ({
  onNext,
  onBack,
  cardata,
  MaintainanceData,
  ImageLink,
  setCount,
  BookingData,
  FromComeData,
}) => {
  const damage_image = MaintainanceData?.damage_image?.split("@@@");
  const [formData, setFormData] = useState({
    Damagenotes: "",
    DamgeImage: [],
    notes: "",
    damageCharges: "",
  });

  const [uploadedDamageImages, setUploadedDamageImages] = useState([]);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const webcamRef = useRef(null);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [carinspectiondata, setCarInspectionData] = useState({});

  const [step3Error, setStep3Error] = useState({
    DamgeImage: false,
    DamgeCharges: false,
    Damgedetail: false,
  });

  useEffect(() => {
    if (MaintainanceData) {
      setFormData((prev) => ({
        ...prev,
        Damagenotes: MaintainanceData.notes_for_future || "",
        notes: MaintainanceData.additional_notes || "",
        damageCharges: MaintainanceData.damage_amount_box || "",
      }));

      // Set damage images from MaintainanceData
      const damageImgs = [
        damage_image[0] || "0",
        damage_image[1] || "0",
        damage_image[2] || "0",
        damage_image[3] || "0",
      ]
        .filter(Boolean)
        .map((item) => ({
          file: null,
          name: item,
        }));

      setCarInspectionData({
        damageImages: damageImgs,
        damageImages_old: damageImgs,
      });
      setUploadedDamageImages(
        damageImgs
          .filter((img) => img.name !== "0")
          .map((img) => (typeof img === "object" && img?.name ? img.name : img))
      );
    }
  }, [MaintainanceData]);

  const handleChangeNotes = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear errors when user starts typing
    if (name === "Damagenotes" && value) {
      setStep3Error((prev) => ({ ...prev, Damgedetail: false }));
    }
  };

  const handleDamageChargesChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, damageCharges: value }));

    if (value) {
      setStep3Error((prev) => ({ ...prev, DamgeCharges: false }));
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length + uploadedDamageImages.length > 4) {
      alert("You can only upload a maximum of 4 images.");
      return;
    }

    const remainingSlots = 4 - uploadedDamageImages.length;
    const filesToUpload = files.slice(0, remainingSlots);

    filesToUpload.forEach((file, index) => {
      uploadImageserver(file, index);
    });
  };

  const handleRemoveImage = (index) => {
    const updatedImages = uploadedDamageImages.filter((_, i) => i !== index);
    setUploadedDamageImages(updatedImages);

    setCarInspectionData((prevData) => {
      const updatedFormImages = (prevData.damageImages || []).filter(
        (_, i) => i !== index
      );
      return {
        ...prevData,
        damageImages: updatedFormImages,
      };
    });
  };

  const openCamera = async () => {
    if (uploadedDamageImages.length >= 4) {
      alert("You can only upload a maximum of 4 images.");
      return;
    }

    try {
      const constraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "environment",
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraOpen(true);
      //console.log("Camera opened successfully")
    } catch (error) {
      console.error("Error opening camera:", error);
      alert(
        "Failed to open camera. Please make sure you've granted camera permission."
      );
    }
  };

  const captureImage = () => {
    if (!webcamRef.current) {
      console.error("Webcam reference is missing");
      return;
    }

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        console.error("Failed to capture screenshot");
        return;
      }

      //console.log("Image captured:", imageSrc.substring(0, 50) + "...")
      const updatedImages = [...uploadedDamageImages, imageSrc].slice(0, 4);
      setUploadedDamageImages(updatedImages);

      // Clear error when image is captured
      if (updatedImages.length > 0) {
        setStep3Error((prev) => ({ ...prev, DamgeImage: false }));
      }

      closeCamera();
    } catch (error) {
      console.error("Error capturing image:", error);
      alert("Failed to capture image. Please try again.");
    }
  };

  const closeCamera = () => {
    setIsCameraOpen(false);
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
    }
    //console.log("Camera modal closed")
  };

  const handleWebcamError = (error) => {
    console.error("Webcam error:", error);
    alert(
      "Failed to access camera. Please make sure you've granted camera permission and that your camera is working properly."
    );
    closeCamera();
  };

  const uploadImageserver = async (file, index) => {
    const fd = new FormData();
    fd.append("image", file);

    try {
      const response = await server_post_data(upload_image_to_folder, fd);
      if (response.data.error) {
        handleError(response.data.message);
      } else {
        const uploadedImageName = response.data.message.image_uploaded;

        // Append image data to formData.damageImages array
        setCarInspectionData((prevData) => {
          const prevImages = prevData.damageImages?.[index];

          return {
            ...prevData,
            damageImages: {
              ...(prevData.damageImages || {}),
              [index]: [
                ...(Array.isArray(prevImages) ? prevImages : []),
                {
                  file,
                  name: uploadedImageName,
                },
              ],
            },
          };
        });

        //console.log("uploadedImageName", uploadedImageName)
        setUploadedDamageImages((prev) => [...prev, uploadedImageName]);

        // Clear any error related to image upload
        setStep3Error((prev) => ({ ...prev, DamgeImage: false }));
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...step3Error };

    // Validate damage images
    if (uploadedDamageImages.length === 0) {
      newErrors.DamgeImage = true;
      isValid = false;
    } else {
      newErrors.DamgeImage = false;
    }

    // Validate damage charges
    if (!formData.damageCharges) {
      newErrors.DamgeCharges = true;
      isValid = false;
    } else {
      newErrors.DamgeCharges = false;
    }

    // Validate damage details
    if (!formData.Damagenotes) {
      newErrors.Damgedetail = true;
      isValid = false;
    } else {
      newErrors.Damgedetail = false;
    }

    setStep3Error(newErrors);
    return isValid;
  };

  const handleNext = async () => {
    if (!validateForm()) {
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("car_id", cardata.primary_id);
    fd.append("notes_for_future", formData.Damagenotes);
    fd.append("damage_amount_box", formData.damageCharges);
    fd.append("additional_notes", formData.notes);
    fd.append("edit_or_new", MaintainanceData?.primary_id || 0);
    fd.append("step_for_repairing", 2);
    fd.append("save_or_edit_type", FromComeData);
    // Append damage images
    Object.values(carinspectiondata.damageImages || {})
      .flat()
      .forEach((imgObj, index) => {
        fd.append(`damage_image${index + 1}`, imgObj.name);
      });
    // Append damage images Old
    Object.values(carinspectiondata.damageImages_old || {})
      .flat()
      .forEach((imgObj, index) => {
        fd.append(`damage_image${index + 1}_old`, imgObj.name);
      });

    try {
      const response = await server_post_data(repairing_car_data, fd);
      if (response.data.error) {
        handleError(response.data.message);
      } else {
        onNext();
        setCount(3);
      }
      setShowLoaderAdmin(false);
    } catch (error) {
      handleError("Network error");
      setShowLoaderAdmin(false);
    }
  };

  return (
    <>
      <div className="safety-checks-container">
        <h3 className="section-title text-primary">
          Damage & Additional Notes <span className="mandory_field">*</span>
        </h3>

        {/* Damage Pictures Section */}
        <div className="section">
          <h5>
            Damage Pictures <span className="mandory_field">*</span>
          </h5>
          <p className="max_upd_img_note">
            You can upload up to 4 images only.
          </p>

          <span
            className="error-text"
            style={{
              color: "red",
              display: step3Error.DamgeImage ? "block" : "none",
            }}
          >
            Please upload at least one damage image
          </span>
          {uploadedDamageImages &&
            uploadedDamageImages.length &&
            console.log(uploadedDamageImages)}
          <div className="media-upload-section">
            <div className="row">
              <div className="col-sm">
                <div className="img_upd_contr">
                  <div
                    className="media-placeholder upload_img_contr"
                    onClick={() => {
                      if (uploadedDamageImages.length < 4) {
                        document.getElementById("damageFileInput").click();
                      }
                    }}
                    style={{
                      opacity: uploadedDamageImages.length >= 4 ? 0.5 : 1,
                      pointerEvents:
                        uploadedDamageImages.length >= 4 ? "none" : "auto",
                    }}
                  >
                    <div className="empty-upload-text">
                      <i className="bi bi-cloud-upload"></i>
                      <span>Browse</span>
                    </div>
                    <input
                      type="file"
                      id="damageFileInput"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </div>
                  <div
                    className="media-placeholder capcureImg"
                    onClick={openCamera}
                    style={{
                      opacity: uploadedDamageImages.length >= 4 ? 0.5 : 1,
                      pointerEvents:
                        uploadedDamageImages.length >= 4 ? "none" : "auto",
                    }}
                  >
                    <div className="empty-upload-text">
                      <i className="bi bi-cloud-upload"></i>
                      <span>Capture</span>
                    </div>
                  </div>
                </div>
              </div>
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="col-sm"
                  style={{ position: "relative" }}
                >
                  {uploadedDamageImages[index] &&
                    uploadedDamageImages[index] !== "0" && (
                      <div
                        className="hoverRemoveImg"
                        onClick={() => handleRemoveImage(index)}
                      >
                        <XIcon className="rmv_icn" />
                      </div>
                    )}
                  <div className="media-placeholder car-image">
                    {uploadedDamageImages[index] &&
                    uploadedDamageImages[index] !== "0" ? (
                      <img
                        src={
                          ImageLink + uploadedDamageImages[index] ||
                          "/placeholder.svg"
                        }
                        alt={`Damage ${index + 1}`}
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                      />
                    ) : (
                      <div
                        className="empty-upload-text"
                        style={{ opacity: "0.5" }}
                      >
                        <UploadIcon size={20} color="#aaa" />
                        <span style={{ marginLeft: "10px" }}>Empty</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Damage Charges Section */}
        <div className="section">
          <div className="row">
            <div className="col-md-4">
              <label className="form-label">
                Damage Charges: <span className="mandory_field">*</span>
              </label>
              {step3Error.DamgeCharges && (
                <span className="error-text d-block" style={{ color: "red" }}>
                  Please enter damage charges
                </span>
              )}
            </div>
            <div className="col-md-8">
              <input
                className="form-control"
                type="text"
                value={formData.damageCharges}
                maxLength={10}
                onChange={handleDamageChargesChange}
                onInput={handleNumbersChange}
                placeholder="Enter Damage Charges"
              />
            </div>
          </div>
        </div>

        {/* Damage Details Section */}
        <div className="section">
          <div className="row">
            <div className="col-md-4">
              <label>
                Damage Details <span className="mandory_field">*</span>
              </label>
              {step3Error.Damgedetail && (
                <span className="error-text d-block" style={{ color: "red" }}>
                  Please enter damage details
                </span>
              )}
            </div>
            <div className="col-md-8">
              <textarea
                name="Damagenotes"
                className="form-control noteSection"
                value={formData.Damagenotes}
                onChange={handleChangeNotes}
                rows="3"
                placeholder="Enter damage details"
              ></textarea>
            </div>
          </div>
        </div>

        {/* Additional Notes Section */}
        <div className="section">
          <div className="row">
            <div className="col-md-4">
              <label>Notes (Additional Comments)</label>
            </div>
            <div className="col-md-8">
              <textarea
                name="notes"
                className="form-control noteSection"
                value={formData.notes}
                onChange={handleChangeNotes}
                rows="2"
                placeholder="Enter additional notes"
              ></textarea>
            </div>
          </div>
        </div>

        {/* Camera Modal */}
        {isCameraOpen && (
          <div
            className="camera-modal"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.9)",
              zIndex: 1000,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <div className="container" style={{ maxWidth: "1200px" }}>
              <div
                className="row"
                style={{
                  backgroundColor: "#222",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <div className="col-8" style={{ padding: "0" }}>
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#000",
                      minHeight: "400px",
                    }}
                  >
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        width: 1280,
                        height: 720,
                        facingMode: "user",
                      }}
                      onUserMediaError={handleWebcamError}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                </div>
                <div
                  className="col-4"
                  style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    backgroundColor: "#333",
                  }}
                >
                  <h4
                    style={{
                      color: "white",
                      marginBottom: "20px",
                      textAlign: "center",
                    }}
                  >
                    Camera Capture
                  </h4>
                  <p
                    style={{
                      color: "#ccc",
                      marginBottom: "30px",
                      textAlign: "center",
                    }}
                  >
                    Position the damage in the frame and click "Take Photo" when
                    ready.
                  </p>
                  <button
                    onClick={captureImage}
                    style={{
                      padding: "12px 0",
                      margin: "10px 0",
                      backgroundColor: "var(--primary-color)",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    Take Photo
                  </button>
                  <button
                    onClick={closeCamera}
                    style={{
                      padding: "12px 0",
                      margin: "10px 0",
                      backgroundColor: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="navigation-buttons">
        <button className="btn back-button" onClick={onBack}>
          Back
        </button>
        <button
          className="btn next__button"
          onClick={handleNext}
          disabled={showLoaderAdmin}
        >
          {showLoaderAdmin ? "Loading..." : "Next"}
        </button>
      </div>
    </>
  );
};

export default RepairStep2;
